import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Text, Grid, GridItem, HStack, Skeleton, SkeletonText, Link as ChakraLink,
  Stack, Link, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalCloseButton, ModalBody, Table, Thead, Tr, Th, Tbody, Td, Spinner,
} from '@chakra-ui/react';
import { useParams, useNavigate, Link as ReactRouterLink } from 'react-router-dom';
import moment from 'moment';
import Navbar from '../elements/Navbar/Navbar';
import Footer from '../elements/Footer';
import ExtendedDocumentPreview from '../elements/ExtendedDocumentPreview';
import FormField from '../elements/FormField';
import {
  updateDocumentValues,
  getDocumentValues,
  updateStepHistory,
  generatePreview,
  hadlePreFilledFields,
  hadleSpMetadata,
  isAuthenticated,
  getDocument,
  getDataUri,
  getDocumentSteps,
  getStepFields,
  handleForcedPrefilledFields,
  loadValidatedPrefilled,
  hadleSpMetadataValidated,
} from '../ServiceRequest';
import { runValidator } from '../utils/validatorFunctions';
import './FormHandler.css';

type VisibleIf = {
  field: string;
  equals: string;
};

type Field = {
  name: string;
  type: string;
  htmlType: string;
  label: string;
  multiple: boolean;
  nullable: boolean;
  options?: string[];
  singleLabel?: string;
  fetch?: string | string[];
  format?: string;
  hardPrefill?: boolean;
  prefilledTrue?: boolean;
  prefilled?: string;
  validatorFunction?: string;
  validatorArguments?: any[];
  computed?: {
    from: string;
    handler: string;
    args?: Record<string, unknown>;
    update?: 'onChange' | 'onBlur' | 'manual';
    fallback?: string;
  };
  visibleIf?: VisibleIf;
};

const isFieldVisibleWithValues = (field: Field, values: Record<string, any>): boolean => {
  const rule = field.visibleIf;
  if (!rule) return true;

  const current = values[rule.field];


  if (current == null) return false;


  if (Array.isArray(current)) {
    return current.includes(rule.equals);
  }

  return String(current) === rule.equals;
};

const orderFieldsForComputed = (fields: Field[]): Field[] => {
  const arr = [...fields];
  for (let pass = 0; pass < arr.length; pass++) {
    let moved = false;
    for (let i = 0; i < arr.length; i++) {
      const f = arr[i];
      if (f.type === 'frontend_calculated' && f.computed?.from) {
        const fromIdx = arr.findIndex((x) => x.name === f.computed!.from);
        if (fromIdx !== -1 && fromIdx > i) {
          const [item] = arr.splice(i, 1);
          arr.splice(fromIdx, 0, item);
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
  return arr;
};

const fetchToKeys = (fetch?: string | string[]): string[] => {
  if (!fetch) return [];
  if (Array.isArray(fetch)) return fetch.map(String).map((s) => s.trim()).filter(Boolean);
  const s = String(fetch).trim();
  if (!s || s.startsWith('sp:')) return [];
  return s.split(',').map((x) => x.trim()).filter(Boolean);
};

const toComposedString = (val: any, sep = ', '): string | undefined => {
  const toParts = (v: any): string[] => {
    if (v == null) return [];
    if (Array.isArray(v)) return v.flatMap(toParts);
    if (typeof v === 'object') {
      return Object.values(v)
        .filter((x) => x != null && typeof x !== 'object')
        .map((x) => String(x).trim())
        .filter(Boolean);
    }
    const s = String(v).trim();
    return s ? [s] : [];
  };
  const parts = toParts(val);
  const out = parts.join(sep).replace(/\s+/g, ' ').trim();
  return out || undefined;
};

const composeFetchedParts = (keys: string[], prefills: Record<string, any>, sep = ', '): string | undefined => {
  if (!keys.length) return undefined;
  const parts: string[] = [];
  for (const k of keys) {
    const s = toComposedString(prefills[k], sep);
    if (s) parts.push(s);
  }
  const out = parts.join(sep).replace(/\s+/g, ' ').trim();
  return out || undefined;
};

const normalizeForCompare = (v: any): any => {
  if (v === undefined) return '__UNDEF__';
  if (v === null) return null;
  if (Array.isArray(v)) return v.map(normalizeForCompare);
  if (typeof File !== 'undefined' && v instanceof File) {
    return { __file__: true, name: v.name, size: v.size, type: v.type };
  }
  if (typeof v === 'object') {
    const keys = Object.keys(v).sort();
    const out: Record<string, any> = {};
    for (const k of keys) out[k] = normalizeForCompare(v[k]);
    return out;
  }
  return v;
};

const deepEqual = (a: any, b: any): boolean =>
  JSON.stringify(normalizeForCompare(a)) === JSON.stringify(normalizeForCompare(b));

const isTrulyEmpty = (v: any): boolean => {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim().length === 0;
  if (Array.isArray(v)) return v.length === 0 || v.every(isTrulyEmpty);
  if (typeof v === 'object') return Object.keys(v).length === 0 || Object.values(v).every(isTrulyEmpty);
  return false;
};

const isEmpty = (v: any): boolean => {
  if (v === 'N/A') return false;
  if (v == null) return true;
  if (typeof File !== 'undefined' && v instanceof File) return !v.size;
  if (Array.isArray(v)) return v.length === 0 || v.every(isEmpty);
  if (typeof v === 'object') {
    const keys = Object.keys(v);
    if (keys.length === 0) return true;
    return keys.every((k) => isEmpty((v as any)[k]));
  }
  if (typeof v === 'string') return v.trim().length === 0;
  return false;
};

const isItemComplete = (item: any): boolean => {
  if (!item || typeof item !== 'object') return false;
  return Object.values(item).every((val) => !isEmpty(val));
};

const isObjectListCompletelyFilled = (field: any, value: any): boolean => {
  if (value === 'N/A') return true;
  if (!Array.isArray(value) || value.length === 0) return false;
  return value.every(isItemComplete);
};

const parseSpFetch = (fetch?: string) => {
  if (!fetch || !fetch.startsWith('sp:')) return null;
  const parts = fetch.split(':');
  const [, sp_field = '', list_name = '', odata_query = ''] = [
    parts[0], parts[1] ?? '', parts[2] ?? '', parts.slice(3).join(':') ?? '',
  ];
  return { sp_field, list_name, odata_query };
};

const normalizeTemplateForField = (raw: any, field: Field): any => {
  if (field.type === 'objectList') {
    const arr = Array.isArray(raw) ? raw : [];
    return arr
      .filter((x) => x && typeof x === 'object')
      .map((x) => {
        const o: Record<string, any> = { ...x };
        for (const k of Object.keys(o)) {
          const v = o[k];
          if (v == null || (typeof v === 'string' && v.trim() === '')) o[k] = '';
        }
        return o;
      });
  }
  return toComposedString(raw, ', ') ?? '';
};

// run custom validator in FE; treat unknown/missing as pass-through
const passesCustomValidator = (field: Field, input: any): boolean => {
  const val = input == null ? '' : String(input);
  const res = runValidator(field.validatorFunction, field.validatorArguments ?? [], val);
  return !!res.ok;
};

const FormBuilder: React.FC = () => {
  const params = useParams<{ project_id: string; document_id: string; template_name: string; step_number: string }>();

  const projectId = params.project_id as string;
  const documentId = params.document_id as string;
  const templateName = params.template_name as string;
  const stepNumber = params.step_number as string;

  const { isOpen, onOpen, onClose } = useDisclosure();
  const formRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();

  const [fields, setFields] = useState<Field[]>([]);
  const [preFilledFields, setPreFilledFields] = useState<Record<string, any>>({});
  const [forcedPrefilled, setForcedPrefilled] = useState<Record<string, any>>({});
  const [savedFields, setSavedFields] = useState<Record<string, any>>({});
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [documentPreview, setDocumentPreview] = useState<string | null>(null);
  const [lastStepUpdate, setLastStepUpdate] = useState<any>({});
  const [step, setStep] = useState<any>({});
  const [stepInfo, setStepInfo] = useState<any>({});
  const [document, setDocument] = useState<any>({});
  const [loadingPreview, setLoadingPreview] = useState<boolean>(false);
  const [formLoading, setFormLoading] = useState<boolean>(true);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [prefillSyncDone, setPrefillSyncDone] = useState(false);

  // for conditional render
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  useEffect(() => {
    setFormValues((prev) => ({ ...savedFields, ...prev }));
  }, [savedFields]);


  const handleFormChange = (ev: React.ChangeEvent<any>) => {
    const target = ev.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
    if (!target || !target.name) return;

    let value: any = target.value;

    // se in futuro hai checkbox ecc. puoi gestirli qui
    // if (target instanceof HTMLInputElement && target.type === 'checkbox') {
    //   value = target.checked;
    // }

    setFormValues((prev) => ({
      ...prev,
      [target.name]: value,
    }));
  };



  useEffect(() => setPrefillSyncDone(false), [documentId]);

  const getCurrentStep = async () => {
    const currentStep = await getDocumentSteps(documentId);
    setStepInfo(currentStep[parseInt(stepNumber) - 1]);
  };

  const getDocumentInfo = async () => {
    const currentDocument = await getDocument(documentId, projectId, templateName);
    setDocument(currentDocument);
  };

  const loadStepFields = async () => {
    try {
      const stepFields = await getStepFields(documentId, stepNumber);
      setFields(stepFields);
    } catch (err) {
      console.error('error while loading fields:', err);
    }
  };

  const getLastStepUpdate = () => {
    const lastStepData = document.data.steps[parseInt(stepNumber) - 1].stepHistory.pop();
    setLastStepUpdate(lastStepData);
    setStep(document.data.steps[parseInt(stepNumber) - 1]);
  };

  const loadPreFilledFields = async () => {
    try {
      const projectPrefilledFields = await hadlePreFilledFields(projectId);
      setPreFilledFields(projectPrefilledFields || {});

      const validatedPrefills = await loadValidatedPrefilled(templateName, fields, projectId);
      setPreFilledFields((prev) => ({ ...prev, ...validatedPrefills }));
    } catch (err) {
      console.error('error while fetching prefilled fields:', err);
    }
  };

  const loadForcedPrefilledFields = async () => {
    try {
      const data = await handleForcedPrefilledFields(documentId, projectId, templateName);
      setForcedPrefilled((data && typeof data === 'object') ? data : {});
    } catch (err) {
      console.error('error while loading forced prefilled fields:', err);
    }
  };

  const loadDocumentValues = async () => {
    try {
      const values = await getDocumentValues(documentId);
      if (values) {
        const saved = await getDocumentValues(documentId);
        setSavedFields(saved);
      }
    } catch (err) {
      console.error('error while fetching document values:', err);
    }
  };

  const initializeForm = async () => {
    setFormLoading(true);
    if (isAuthenticated()) {
      await loadPreFilledFields();
      await loadForcedPrefilledFields();
      await loadStepFields();
      await loadDocumentValues();
    }
    setFormLoading(false);
  };

  const buildPayload = async (): Promise<Record<string, any>> => {
    if (!formRef.current) return {};
    const formData = new FormData(formRef.current);
    const raw: Record<string, any> = {};

    for (const [key] of Array.from(formData.entries())) {
      const all = formData.getAll(key);
      const fieldValue = all.length > 1 ? all : formData.get(key);
      raw[key] = preFilledFields[key] || fieldValue || '';
    }

    // files to data-uri
    for (const key of Object.keys(raw)) {
      const value = raw[key];
      if (typeof File !== 'undefined' && value instanceof File) {
        if (value.size) raw[key] = await getDataUri(value); else delete raw[key];
        continue;
      }
      if (Array.isArray(value)) {
        const arr = [...value];
        for (let i = 0; i < arr.length; i++) {
          const v = arr[i];
          if (typeof File !== 'undefined' && v instanceof File) arr[i] = v.size ? await getDataUri(v) : '';
        }
        raw[key] = arr;
      }
    }

    // try parse json-like strings
    for (const k of Object.keys(raw)) {
      const v = raw[k];
      if (typeof v === 'string') {
        const s = v.trim();
        if (s !== 'N/A' && ((s.startsWith('[') && s.endsWith(']')) || (s.startsWith('{') && s.endsWith('}')))) {
          try { raw[k] = JSON.parse(s); } catch { }
        }
      }
    }

    // normalize objectList empties
    for (const [key, value] of Object.entries(raw)) {
      if (Array.isArray(value)) {
        const arr = [...value];
        for (let i = 0; i < arr.length; i++) {
          const item = arr[i];
          if (item && typeof item === 'object' && !Array.isArray(item)) {
            const normalized: Record<string, any> = { ...item };
            for (const [subKey, subValue] of Object.entries(normalized)) {
              if (isTrulyEmpty(subValue)) normalized[subKey] = '';
            }
            arr[i] = normalized;
          }
        }
        raw[key] = arr;
      }
    }

    // forced-prefill (template) — apply only if valid and not overriding user input
    for (const f of fields) {
      const tplKey = f.prefilled || f.name;
      const tplRaw = forcedPrefilled?.[tplKey];
      if (tplRaw === undefined) continue;

      const tplVal = normalizeTemplateForField(tplRaw, f);
      const savedVal = savedFields[f.name];

      if (!isEmpty(savedVal)) { raw[f.name] = savedVal; continue; }
      if (isEmpty(tplVal)) continue;

      // validate candidate before applying
      if (!passesCustomValidator(f, tplVal)) continue;

      raw[f.name] = tplVal;
    }

    return raw;
  };

  const handleSaveStep = async () => {
    setSubmitted(true);
    const payload = await buildPayload();

    const missing = orderFieldsForComputed(fields)
      .filter((f) => {

        if (!isFieldVisibleWithValues(f, payload)) return false;

        const val = (payload as any)[f.name];
        if (f.type === 'objectList') {
          if (f.nullable && val === 'N/A') return false;
          return !isObjectListCompletelyFilled(f, val);
        }
        if (f.nullable) {
          if (val === 'N/A') return false;
          return isEmpty(val);
        }
        return isEmpty(val);
      })
      .map((f) => ({ name: f.name, label: f.label }));

    if (missing.length > 0) {
      setInvalidFields(new Set(missing.map((m) => m.name)));
      const first = missing[0]?.name;
      if (first) {
        requestAnimationFrame(() => {
          const el: HTMLElement | null | undefined =
            formRef.current?.querySelector(`[data-field-name="${CSS.escape(first)}"]`);
          if (el) (el as any).scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        });
      }
      return;
    }

    try {
      setInvalidFields(new Set());
      await updateDocumentValues(documentId, payload);
      await updateStepHistory(documentId, parseInt(stepNumber, 10));
      navigate(`/${projectId}/${documentId}/${templateName}`);
    } catch (err) {
      console.error('error while saving data:', err);
    }
  };

  const generateDocumentPreview = async (highlight: boolean) => {
    const payload = await buildPayload();
    try {
      setLoadingPreview(true);
      const previewUrl = await generatePreview(documentId, payload, highlight);
      setDocumentPreview(previewUrl);
    } catch (err) {
      console.error('preview generation error:', err);
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    const initializePage = async () => {
      await initializeForm();
      await getDocumentInfo();
      await getCurrentStep();
      await generateDocumentPreview(false);
    };
    initializePage();
  }, [templateName, stepNumber, documentId]);

  useEffect(() => {
    if (document.data) getLastStepUpdate();
  }, [document]);

  // prefill autosync (SP + server) with validation before auto-writing
  useEffect(() => {
    if (formLoading) return;
    if (prefillSyncDone) return;

    const ready = fields.length > 0 && savedFields !== undefined;
    if (!ready) return;

    const run = async () => {
      try {
        const spFetchesToLoad: Array<{ key: string; sp_field: string; list_name: string; odata_query: string }> = [];
        const keyToTemplateFieldName: Record<string, string> = {};

        // discover sp fetches
        for (const f of fields) {
          if (!f.fetch || Array.isArray(f.fetch)) continue;
          const parsed = parseSpFetch(f.fetch);
          if (!parsed) continue;
          if (!(f.fetch in preFilledFields)) {
            spFetchesToLoad.push({ key: f.fetch, ...parsed });
            keyToTemplateFieldName[f.fetch] = f.name;
          }
        }

        let spPrefillsByFetch: Record<string, any> = {};
        let spPrefillsByName: Record<string, any> = {};

        if (spFetchesToLoad.length > 0) {
          /* const results = await Promise.all(
            spFetchesToLoad.map(({ key, sp_field, list_name, odata_query }) =>
              hadleSpMetadata(sp_field, keyToTemplateFieldName[key], projectId, list_name, odata_query ?? '')
                .then((val) => ({ key, val }))
                .catch((err) => {
                  console.error('[sp prefill] error on', key, err);
                  return { key, val: undefined };
                })
            )
          ); */

          const results = await Promise.all(
            spFetchesToLoad.map(({ key, sp_field, list_name, odata_query }) =>
              hadleSpMetadataValidated(templateName, sp_field, keyToTemplateFieldName[key], projectId, list_name, odata_query ?? "")
                .then((val) => ({ key, val }))
                .catch(() => ({ key, val: undefined }))
            )
          );

          for (const r of results) {
            const name = keyToTemplateFieldName[r.key];
            const val = r.val;
            const hasValue = val !== undefined && val !== null && !(typeof val === 'string' && val.trim() === '');
            if (hasValue) {
              spPrefillsByFetch[r.key] = val;
              if (name) spPrefillsByName[name] = val;
            }
          }

          if (Object.keys(spPrefillsByFetch).length || Object.keys(spPrefillsByName).length) {
            setPreFilledFields((prev) => ({ ...prev, ...spPrefillsByFetch, ...spPrefillsByName }));
          }
        }

        const mergedPrefills: Record<string, any> = {
          ...preFilledFields,
          ...spPrefillsByFetch,
          ...spPrefillsByName,
        };

        const delta: Record<string, any> = {};

        // 1) single key fetch (non csv, non sp:) — apply if valid and different
        for (const f of fields) {
          if (!f.fetch || Array.isArray(f.fetch)) continue;
          const isCsv = String(f.fetch).includes(',') && !String(f.fetch).startsWith('sp:');
          if (isCsv || String(f.fetch).startsWith('sp:')) continue;

          const prefillKey = String(f.fetch);
          const serverVal = mergedPrefills[prefillKey];
          if (serverVal === undefined || isEmpty(serverVal)) continue;

          // validate candidate
          if (!passesCustomValidator(f, serverVal)) continue;

          const savedVal = savedFields[f.name];
          if (!deepEqual(serverVal, savedVal)) delta[f.name] = serverVal;
        }

        // 2) composed fetch (csv list of keys) — apply if valid and different
        for (const f of fields) {
          const keys = fetchToKeys(f.fetch);
          if (!keys.length) continue;
          const composed = composeFetchedParts(keys, mergedPrefills, ', ');
          if (composed === undefined || isEmpty(composed)) continue;

          if (!passesCustomValidator(f, composed)) continue;

          const savedVal = savedFields[f.name];
          if (!deepEqual(composed, savedVal)) delta[f.name] = composed;
        }

        // 3) forced prefill from template (hardPrefill/prefilledTrue) — write only when valid and user didn't fill yet
        for (const f of fields) {
          const tplKey = f.prefilled || f.name;
          const tplRaw = forcedPrefilled?.[tplKey];
          if (tplRaw === undefined) continue;

          const formatted = normalizeTemplateForField(tplRaw, f);
          if (isEmpty(formatted)) continue;

          if (!passesCustomValidator(f, formatted)) continue;

          const savedVal = savedFields[f.name];
          if (isEmpty(savedVal) && !deepEqual(formatted, savedVal)) {
            delta[f.name] = formatted;
          }
        }

        if (Object.keys(delta).length === 0) {
          setPrefillSyncDone(true);
          return;
        }

        await updateDocumentValues(documentId, delta);
        setSavedFields((prev) => ({ ...prev, ...delta }));
      } catch (e) {
        console.error('prefill autosync failed:', e);
      } finally {
        setPrefillSyncDone(true);
      }
    };

    run();
  }, [formLoading, fields, preFilledFields, forcedPrefilled, savedFields, documentId, prefillSyncDone, projectId]);

  return (
    <>
      <Navbar />
      <Box className="container">
        <Grid templateColumns="repeat(12, 1fr)" gap={4}>
          <GridItem colSpan={{ base: 12, md: 6 }}>
            <div className="doc-toolbar">
              <ChakraLink as={ReactRouterLink} to={`/${projectId}/${documentId}/${templateName}`}>
                Back to document
              </ChakraLink>
            </div>

            {!document.data ? (
              <SkeletonText noOfLines={2} spacing="4" skeletonHeight="2" />
            ) : (
              <div className="step-information">
                <Text fontSize="xl" as="h1">{document.data.name}</Text>
                <Text fontSize="xs">
                  Work Order #{document.data._id} (Project ID: <b>{document.data.projectId})</b>
                </Text>
                <Text fontSize="2xl" as="h1">{stepInfo.stepName}</Text>
                <Text fontSize="sm" as="h1">{stepInfo.stepDescription}</Text>
                <Stack spacing={1} style={{ marginBottom: '10px' }}>
                  <Text fontSize="sm">{document.description}</Text>
                  {lastStepUpdate && (
                    <HStack spacing="30px">
                      <Text fontSize="xs">
                        Last compiled by <b>{lastStepUpdate.userAlias}</b> ({lastStepUpdate.userName}) at{' '}
                        {moment(lastStepUpdate.timestamp).format('MMMM Do YYYY, h:mm A')} (
                        {moment(lastStepUpdate.timestamp).local().fromNow()})
                        <Link onClick={onOpen}>&nbsp;<b>History</b></Link>
                      </Text>
                    </HStack>
                  )}
                </Stack>
              </div>
            )}

            {formLoading ? (
              <Skeleton height="40px" my="10px" speed={2} />
            ) : (
              <Box
                as="form"
                ref={formRef}
                noValidate
                onChange={handleFormChange}
                onSubmit={(e: any) => { e.preventDefault(); handleSaveStep(); }}
              >
                {orderFieldsForComputed(fields).map((field) => {
                  const savedVal = savedFields[field.name];

                  // candidate from template (forced)
                  const tplKey = field.prefilled || field.name;
                  const tplRaw = forcedPrefilled?.[tplKey];
                  const tplVal = tplRaw === undefined ? undefined : normalizeTemplateForField(tplRaw, field);
                  const tplValid = tplVal !== undefined && !isEmpty(tplVal) && passesCustomValidator(field, tplVal);

                  // candidate from soft prefill (db/sp)
                  let softPrefill: any;
                  const keys = fetchToKeys(field.fetch);
                  if (keys.length) {
                    softPrefill = composeFetchedParts(keys, preFilledFields, ', ');
                  } else {
                    const prefillKey =
                      (!field.fetch || Array.isArray(field.fetch) || String(field.fetch).includes(','))
                        ? field.name
                        : String(field.fetch);
                    softPrefill = preFilledFields[prefillKey];
                  }
                  const softValid = softPrefill !== undefined && !isEmpty(softPrefill) && passesCustomValidator(field, softPrefill);

                  // choose value: saved > valid template > valid soft > ''
                  const chosen =
                    !isEmpty(savedVal) ? savedVal :
                      tplValid ? tplVal :
                        softValid ? softPrefill :
                          '';

                  // readOnly only when frontend_calculated OR (hard prefill applied with a valid value)
                  const isHard = !!(field.hardPrefill || field.prefilledTrue);
                  const readOnly =
                    field.type === 'frontend_calculated' ||
                    (isHard && !isEmpty(chosen) && (tplValid || softValid));

                  const visibilityValues: Record<string, any> = {
                    ...preFilledFields,
                    ...savedFields,
                    ...formValues,
                  };

                  const visible = isFieldVisibleWithValues(field, visibilityValues);
                  if (!visible) {
                    return null;
                  }

                  return (
                    <FormField
                      key={field.name}
                      field={field}
                      value={chosen}
                      readOnly={readOnly}
                      form={formRef}
                      invalid={invalidFields.has(field.name)}
                      templateName={templateName}
                      submitted={submitted}
                    />
                  );
                })}

                <HStack spacing={4} mt={5} justify="center">
                  <Button className="el-btn el-btn-secondary" onClick={() => navigate(`/${projectId}/${documentId}/${templateName}`)}>
                    Cancel
                  </Button>
                  <Button className="el-btn" type="submit" onClick={handleSaveStep}>
                    Save
                  </Button>
                </HStack>
              </Box>
            )}
          </GridItem>

          <GridItem colSpan={{ base: 12, md: 6 }}>
            <div className="form-preview-header">
              <Text fontSize="2xl" as="h1">Document preview</Text>
              <Button className="el-btn" onClick={() => { generateDocumentPreview(true); }}>
                Update Preview
              </Button>
            </div>
            <Box overflowY="auto" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
              {loadingPreview ? (
                <div className="preview-placeholder"><Spinner size="xl" /></div>
              ) : documentPreview ? (
                <ExtendedDocumentPreview fileUrl={documentPreview} />
              ) : (
                <div className="preview-placeholder"><Spinner size="xl" /></div>
              )}
            </Box>
          </GridItem>
        </Grid>
      </Box>

      <Modal isOpen={isOpen} size="full" onClose={onClose} isCentered>
        <ModalOverlay bg="none" backdropFilter="auto" backdropBlur="10px" />
        <ModalContent className="modal" borderRadius="0px" boxShadow="xl" maxWidth="800px">
          <ModalHeader>{stepInfo?.stepName} history</ModalHeader>
          <ModalCloseButton className="modal-close-button" />
          <ModalBody className="modal-body">
            <Table variant="simple" size="sm">
              <Thead><Tr><Th>Updated by</Th><Th>At</Th></Tr></Thead>
              <Tbody className="table">
                {step?.stepHistory?.map((history: any, idx: number) => (
                  <Tr key={idx}>
                    <Td>{history.userAlias} <small>({history.userName})</small></Td>
                    <Td>
                      {moment(history.timestamp).format('MMMM Do YYYY, h:mm A')}{' '}
                      <small>({moment(history.timestamp).local().fromNow()})</small>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            {step?.stepHistory?.length === 0 && <p className="no-data-message">No data available</p>}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Footer />
    </>
  );
};

export default FormBuilder;
